/**
 * Shared form types used by both Questionnaire and Editor contexts
 * This ensures consistent data structure across different form implementations
 */

import { StorageContext, ImageKey } from '../../../../services/indexedDBService';

// FAQ item interface
export interface FAQItem {
    id: string;
    question: string;
    answer: string;
    isCustom: boolean; // true if user added their own question
}

// Witness interface
export interface Witness {
    name: string;
    image: boolean; // true if image exists in IndexedDB
}

export type MenuIconType = 'emoji' | 'mui';

export const FOOD_MUI_ICONS = [
    'Restaurant', 'LocalBar', 'LocalCafe', 'LocalPizza', 'LunchDining',
    'BakeryDining', 'RamenDining', 'Icecream', 'Cake', 'SetMeal',
    'Tapas', 'SoupKitchen', 'KebabDining', 'LocalDining',
    'WineBar', 'EmojiFoodBeverage', 'DinnerDining', 'BrunchDining',
    'EggAlt', 'RiceBowl'
] as const;

export type FoodMuiIcon = typeof FOOD_MUI_ICONS[number];

export interface MenuSection {
    id: string;
    title: string;
    iconType: MenuIconType;
    iconValue: string;
    details: string;
    expandInDialog: boolean;
}

export const WEDDING_MUI_ICONS = [
    'Church', 'Celebration', 'Favorite', 'FavoriteBorder', 'Diamond',
    'Groups', 'MusicNote', 'PhotoCamera', 'CameraAlt', 'DirectionsCar',
    'Park', 'LocalFlorist', 'Nightlife', 'SportsBar', 'CardGiftcard',
    'Checkroom', 'TheaterComedy', 'Spa', 'FilterVintage', 'AutoAwesome',
    'Stars', 'EmojiPeople', 'DirectionsWalk', 'Schedule', 'Place',
    'Restaurant', 'Cake', 'LocalBar', 'WineBar', 'NightlightRound'
] as const;

export type WeddingMuiIcon = typeof WEDDING_MUI_ICONS[number];

export interface ScheduleEvent {
    id: string;
    title: string;
    time: string; // HH:mm format
    iconType: MenuIconType;
    iconValue: string;
}

export interface WeddingTableGuest {
    id: string;
    name: string;
}

export interface WeddingTable {
    id: string;
    name: string;
    guests: WeddingTableGuest[];
}

// Form data interface
export interface QuestionnaireFormData {
    currentStep: number; // Current step index
    basicInfo: {
        coupleName1: string;
        coupleName2: string;
        weddingDate: string; // ISO date string
        weddingTime: string; // HH:MM format
    };
    mainImages: {
        desktopImage: boolean; // true if image exists in IndexedDB
        mobileImage: boolean; // true if image exists in IndexedDB
    };
    ceremonyLocation: {
        location: string; // Google Places location
        details: string; // Parking + event description combined
    };
    weddingLocation: {
        isSameLocation: boolean; // Is wedding at same location as ceremony
        location: string; // Google Places location (if different)
        details: string; // Parking + event description combined
    };
    guestsFAQ: {
        enabled: boolean;
        faqs: FAQItem[];
        selectedQuestionId: string | null;
    };
    witnesses: {
        enabled: boolean;
        witness1: Witness;
        witness2: Witness;
    };
    contact: {
        email1: string;
        phone1: string;
        email2: string;
        phone2: string;
    };
    domain: {
        type: 'subdomain' | 'custom' | '';
        subdomainName: string;
        customDomain: string;
        letUsBuyDomain: boolean;
    };
    menu: {
        enabled: boolean;
        stayTuned: boolean;
        sections: MenuSection[];
    };
    schedule: {
        enabled: boolean;
        stayTuned: boolean;
        events: ScheduleEvent[];
    };
    colors: {
        primaryColor: string;
        secondaryColor: string;
    };
    tables: {
        published: boolean;
        tables: WeddingTable[];
    };
    weddingDetails: {
        // Future fields will be added here
    };
}

// Initial form data
export const initialFormData: QuestionnaireFormData = {
    currentStep: 0,
    basicInfo: {
        coupleName1: '',
        coupleName2: '',
        weddingDate: '',
        weddingTime: ''
    },
    mainImages: {
        desktopImage: false,
        mobileImage: false
    },
    ceremonyLocation: {
        location: '',
        details: ''
    },
    weddingLocation: {
        isSameLocation: true,
        location: '',
        details: ''
    },
    guestsFAQ: {
        enabled: true,
        faqs: [],
        selectedQuestionId: null
    },
    witnesses: {
        enabled: true,
        witness1: {
            name: '',
            image: false
        },
        witness2: {
            name: '',
            image: false
        }
    },
    contact: {
        email1: '',
        phone1: '',
        email2: '',
        phone2: ''
    },
    domain: {
        type: '',
        subdomainName: '',
        customDomain: '',
        letUsBuyDomain: true
    },
    menu: {
        enabled: false,
        stayTuned: false,
        sections: []
    },
    schedule: {
        enabled: false,
        stayTuned: false,
        events: []
    },
    colors: {
        primaryColor: '#C45526',
        secondaryColor: '#94AE98'
    },
    tables: {
        published: false,
        tables: []
    },
    weddingDetails: {}
};

// Action types
export type FormAction = 
    | { type: 'UPDATE_BASIC_INFO'; payload: Partial<QuestionnaireFormData['basicInfo']> }
    | { type: 'UPDATE_MAIN_IMAGES'; payload: Partial<QuestionnaireFormData['mainImages']> }
    | { type: 'UPDATE_CEREMONY_LOCATION'; payload: Partial<QuestionnaireFormData['ceremonyLocation']> }
    | { type: 'UPDATE_WEDDING_LOCATION'; payload: Partial<QuestionnaireFormData['weddingLocation']> }
    | { type: 'UPDATE_GUESTS_FAQ'; payload: Partial<QuestionnaireFormData['guestsFAQ']> }
    | { type: 'ADD_FAQ_ITEM'; payload: FAQItem }
    | { type: 'UPDATE_FAQ_ANSWER'; payload: { id: string; answer: string } }
    | { type: 'REMOVE_FAQ_ITEM'; payload: string }
    | { type: 'UPDATE_WITNESSES'; payload: Partial<QuestionnaireFormData['witnesses']> }
    | { type: 'UPDATE_CONTACT'; payload: Partial<QuestionnaireFormData['contact']> }
    | { type: 'UPDATE_DOMAIN'; payload: Partial<QuestionnaireFormData['domain']> }
    | { type: 'UPDATE_MENU'; payload: Partial<QuestionnaireFormData['menu']> }
    | { type: 'UPDATE_SCHEDULE'; payload: Partial<QuestionnaireFormData['schedule']> }
    | { type: 'UPDATE_COLORS'; payload: Partial<QuestionnaireFormData['colors']> }
    | { type: 'UPDATE_TABLES'; payload: Partial<QuestionnaireFormData['tables']> }
    | { type: 'UPDATE_WEDDING_DETAILS'; payload: Partial<QuestionnaireFormData['weddingDetails']> }
    | { type: 'UPDATE_CURRENT_STEP'; payload: number }
    | { type: 'LOAD_FROM_STORAGE'; payload: QuestionnaireFormData }
    | { type: 'RESET_FORM' };

// Context interface - used by both Questionnaire and Editor
export interface FormContextType {
    formData: QuestionnaireFormData;
    storageContext: StorageContext; // NEW: Determines which IndexedDB storage to use
    updateBasicInfo: (data: Partial<QuestionnaireFormData['basicInfo']>) => void;
    updateMainImages: (data: Partial<QuestionnaireFormData['mainImages']>) => void;
    updateCeremonyLocation: (data: Partial<QuestionnaireFormData['ceremonyLocation']>) => void;
    updateWeddingLocation: (data: Partial<QuestionnaireFormData['weddingLocation']>) => void;
    updateGuestsFAQ: (data: Partial<QuestionnaireFormData['guestsFAQ']>) => void;
    addFAQItem: (faq: FAQItem) => void;
    updateFAQAnswer: (id: string, answer: string) => void;
    removeFAQItem: (id: string) => void;
    updateWitnesses: (data: Partial<QuestionnaireFormData['witnesses']>) => void;
    updateContact: (data: Partial<QuestionnaireFormData['contact']>) => void;
    updateDomain: (data: Partial<QuestionnaireFormData['domain']>) => void;
    updateMenu: (data: Partial<QuestionnaireFormData['menu']>) => void;
    updateSchedule: (data: Partial<QuestionnaireFormData['schedule']>) => void;
    updateColors: (data: Partial<QuestionnaireFormData['colors']>) => void;
    updateTables: (data: Partial<QuestionnaireFormData['tables']>) => void;
    updateWeddingDetails: (data: Partial<QuestionnaireFormData['weddingDetails']>) => void;
    updateCurrentStep: (step: number) => void;
    loadFormData: (data: QuestionnaireFormData) => void;
    resetForm: () => void;
    markImageChanged: (key: ImageKey) => void;
}
